import { formatMoney } from '@theme/money-formatting';

/**
 * @typedef {{variantId: string, quantity: number}} BundleItem
 */

const DEFAULT_MAX_QUANTITY = 99;

/**
 * Lets shoppers pick individual variants (with a stepper) next to the featured deal radio.
 *
 * While no row has a quantity the component is in `deal` mode and the theme's variant flow owns
 * the price and buttons. Once a row gets a quantity it switches to `custom` mode: the page price
 * and add-to-cart buttons show the combined total and the product form adds every row as its own
 * cart line (see `getItems`, used by product-form.js). Selecting the deal again clears the rows.
 *
 * The mode lives in `data-mode` inside the variant picker markup, so a section re-render from the
 * variant picker (which always renders the deal state) resets it consistently with the rows.
 */
class BundleSelectorComponent extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;

  /**
   * Price markup captured when entering custom mode, restored when going back to the deal.
   * @type {Map<Element, string>}
   */
  #originalPrices = new Map();

  connectedCallback() {
    this.#abortController = new AbortController();
    this.addEventListener('click', this.#onClick, { signal: this.#abortController.signal });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  /** @returns {'deal' | 'custom'} */
  get mode() {
    return this.dataset.mode === 'custom' ? 'custom' : 'deal';
  }

  /**
   * The lines to add to the cart, or null when the deal is selected and the regular flow applies.
   * @returns {BundleItem[] | null}
   */
  getItems() {
    if (this.mode !== 'custom') return null;

    const items = this.#rows
      .map((row) => ({ variantId: row.dataset.variantId ?? '', quantity: this.#getQuantity(row) }))
      .filter((item) => item.variantId && item.quantity > 0);

    return items.length > 0 ? items : null;
  }

  /** @returns {HTMLElement[]} */
  get #rows() {
    return /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-bundle-row]')));
  }

  /** @returns {HTMLInputElement | null} */
  get #dealInput() {
    return this.querySelector('.bundle-selector__featured .bundle-selector__input');
  }

  /**
   * @param {HTMLElement} row
   * @returns {number}
   */
  #getQuantity(row) {
    return Number(row.dataset.quantity) || 0;
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const button = target.closest('[data-bundle-action]');
    const row = button?.closest('[data-bundle-row]');

    if (button instanceof HTMLButtonElement && row instanceof HTMLElement && this.contains(row)) {
      const quantity = this.#getQuantity(row);
      const action = button.dataset.bundleAction;

      if (action === 'add' || action === 'increase') this.#setQuantity(row, quantity + 1);
      if (action === 'decrease') this.#setQuantity(row, quantity - 1);
      return;
    }

    // The deal radio is still checked in custom mode, so clicking it fires no change event.
    if (this.mode === 'custom' && target.closest('.bundle-selector__featured')) {
      this.#selectDeal();
    }
  };

  /**
   * @param {HTMLElement} row
   * @param {number} quantity
   */
  #setQuantity(row, quantity) {
    const max = Number(row.dataset.max) || DEFAULT_MAX_QUANTITY;
    const next = Math.max(0, Math.min(quantity, max));

    this.#renderRow(row, next);

    if (this.#rows.every((item) => this.#getQuantity(item) === 0)) {
      this.#selectDeal();
      return;
    }

    if (this.mode !== 'custom') {
      this.#captureOriginalPrices();
      this.dataset.mode = 'custom';
    }

    this.#renderPrices();
  }

  /**
   * @param {HTMLElement} row
   * @param {number} quantity
   */
  #renderRow(row, quantity) {
    const max = Number(row.dataset.max) || DEFAULT_MAX_QUANTITY;
    const quantityElement = row.querySelector('[data-bundle-quantity]');
    const increase = row.querySelector('[data-bundle-action="increase"]');

    row.dataset.quantity = String(quantity);
    if (quantityElement) quantityElement.textContent = String(quantity);
    if (increase instanceof HTMLButtonElement) increase.disabled = quantity >= max;
  }

  #selectDeal() {
    for (const row of this.#rows) this.#renderRow(row, 0);

    this.dataset.mode = 'deal';

    if (this.#originalPrices.size > 0) {
      this.#restorePrices();
      return;
    }

    // The page was loaded on one of the rows, so the deal radio is not checked and the regular
    // variant flow has to re-render the page. When this runs from a click on the radio itself it
    // is already checked and its change event does that.
    const dealInput = this.#dealInput;
    if (dealInput && !dealInput.checked) dealInput.click();
  }

  /** @returns {Element[]} */
  #getPriceTargets() {
    const scope = this.closest('.shopify-section') ?? document;
    return Array.from(scope.querySelectorAll('product-price [ref="priceContainer"], .add-to-cart__price-group'));
  }

  #captureOriginalPrices() {
    this.#originalPrices.clear();
    for (const target of this.#getPriceTargets()) {
      this.#originalPrices.set(target, target.innerHTML);
    }
  }

  #restorePrices() {
    for (const [target, markup] of this.#originalPrices) {
      if (target.isConnected) target.innerHTML = markup;
    }
    this.#originalPrices.clear();
  }

  #renderPrices() {
    let price = 0;
    let compareAtPrice = 0;

    for (const row of this.#rows) {
      const quantity = this.#getQuantity(row);
      const rowPrice = Number(row.dataset.price) || 0;
      const rowCompareAtPrice = Number(row.dataset.compareAtPrice) || 0;

      price += rowPrice * quantity;
      compareAtPrice += Math.max(rowCompareAtPrice, rowPrice) * quantity;
    }

    const { moneyFormat = '{{amount}}', currency = '' } = this.dataset;
    const priceText = formatMoney(price, moneyFormat, currency);
    const compareAtText = compareAtPrice > price ? formatMoney(compareAtPrice, moneyFormat, currency) : null;

    for (const target of this.#getPriceTargets()) {
      if (target.classList.contains('add-to-cart__price-group')) {
        target.replaceChildren(...this.#buildButtonPrice(priceText, compareAtText));
      } else {
        target.replaceChildren(...this.#buildPagePrice(priceText, compareAtText));
      }
    }
  }

  /**
   * Mirrors the markup of snippets/price.liquid.
   * @param {string} priceText
   * @param {string | null} compareAtText
   * @returns {HTMLElement[]}
   */
  #buildPagePrice(priceText, compareAtText) {
    if (!compareAtText) return [createElement('span', 'price', priceText)];

    const { labelRegular = '', labelSale = '' } = this.dataset;
    const regular = createElement('span', 'price-item__group price-item--regular compare-at-price');
    regular.append(createElement('span', 'visually-hidden', `${labelRegular} `), compareAtText);
    const sale = createElement('span', 'price-item__group price');
    sale.append(createElement('span', 'visually-hidden', `${labelSale} `), priceText);

    return [regular, sale];
  }

  /**
   * Mirrors the markup of snippets/add-to-cart-price.liquid.
   * @param {string} priceText
   * @param {string | null} compareAtText
   * @returns {HTMLElement[]}
   */
  #buildButtonPrice(priceText, compareAtText) {
    const elements = [];
    if (compareAtText) elements.push(createElement('s', 'add-to-cart__compare-price', compareAtText));
    elements.push(createElement('span', 'add-to-cart__price', priceText));

    return elements;
  }
}

/**
 * @param {string} tagName
 * @param {string} className
 * @param {string} [text]
 * @returns {HTMLElement}
 */
function createElement(tagName, className, text) {
  const element = document.createElement(tagName);
  element.className = className;
  if (text !== undefined) element.textContent = text;
  return element;
}

if (!customElements.get('bundle-selector-component')) {
  customElements.define('bundle-selector-component', BundleSelectorComponent);
}
